import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import api from "../../api";

// NL Labs' own finance (the platform org has no doctors, so the clinic
// Finance view means nothing for it): subscription revenue, AI cost, profit,
// and the patient money held for clinics. Backend: billing/platform_finance_routes.py.
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function presetRange(key) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  if (key === "this_month") return [isoDate(new Date(Date.UTC(y, m, 1))), isoDate(today)];
  if (key === "last_month") return [isoDate(new Date(Date.UTC(y, m - 1, 1))), isoDate(new Date(Date.UTC(y, m, 0)))];
  if (key === "last_3") return [isoDate(new Date(Date.UTC(y, m - 2, 1))), isoDate(today)];
  // Indian financial year, April to March
  const fyStart = m >= 3 ? y : y - 1;
  return [isoDate(new Date(Date.UTC(fyStart, 3, 1))), isoDate(today)];
}

const PRESETS = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_3", label: "Last 3 months" },
  { key: "fy", label: "This financial year" },
];

const SOURCES = [
  { key: "subscriptions", label: "Subscriptions" },
  { key: "ai_addon", label: "AI add-on" },
  { key: "ai_dues", label: "AI dues paid" },
  { key: "ai_from_payouts", label: "AI fees kept from payouts" },
];

function Stat({ label, value, hint, tone = "text-secondary-900" }) {
  return (
    <div className="rounded-xl border border-secondary-200 bg-white p-4">
      <p className="text-xs font-medium text-secondary-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
      {hint && <p className="text-xs text-secondary-500 mt-1">{hint}</p>}
    </div>
  );
}

export default function PlatformFinance() {
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(() => presetRange("this_month"));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    api.fetchPlatformFinance(range[0], range[1])
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || "Couldn't load the figures."));
  }, [range]);

  const pick = (key) => {
    setPreset(key);
    setRange(presetRange(key));
  };

  const s = data?.subscriptions;
  const pm = data?.patient_money;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <button key={p.key} type="button" onClick={() => pick(p.key)}
            className={`px-3 py-1.5 rounded-lg text-sm ${preset === p.key ? "bg-primary-600 text-white" : "bg-white border border-secondary-200 text-secondary-700"}`}>
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-2 text-sm">
          <input type="date" value={range[0]} max={range[1]} onChange={(e) => { setPreset(null); setRange([e.target.value, range[1]]); }}
            className="rounded-lg border border-secondary-300 px-2 py-1.5" />
          <span className="text-secondary-500">to</span>
          <input type="date" value={range[1]} min={range[0]} onChange={(e) => { setPreset(null); setRange([range[0], e.target.value]); }}
            className="rounded-lg border border-secondary-300 px-2 py-1.5" />
        </div>
      </div>

      {error && <p className="text-red-600">{error}</p>}
      {!data && !error && <p className="text-secondary-500">Loading…</p>}

      {data && (
        <>
          <section className="grid gap-4 grid-cols-1 sm:grid-cols-3">
            <Stat label="Revenue (excl. GST)" value={inr(data.revenue.total)}
              hint={`${inr(data.revenue.total_with_gst)} received, of which ${inr(data.revenue.gst)} is GST`} />
            <Stat label="AI model cost" value={inr(data.costs.ai_models)}
              hint={`From logged AI usage at ₹${data.costs.usd_inr_rate}/USD${data.costs.ai_models_platform ? `; ${inr(data.costs.ai_models_platform)} of it NL Labs' own use` : ""}`} />
            <Stat label="Profit" value={inr(data.profit)} tone={data.profit < 0 ? "text-red-600" : "text-emerald-700"}
              hint="Revenue minus AI model cost" />
          </section>
          <p className="text-xs text-secondary-500 -mt-3">
            Costs not recorded per payment are not included: payment gateway fees on subscriptions, transcription, SMS, video and hosting.
          </p>

          <section className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-secondary-200 bg-white p-4">
              <h2 className="font-semibold text-secondary-900">Where the revenue came from</h2>
              <ul className="mt-3 divide-y divide-secondary-100">
                {SOURCES.map((src) => (
                  <li key={src.key} className="flex justify-between py-2 text-sm">
                    <span className="text-secondary-700">{src.label}</span>
                    <span className="font-medium text-secondary-900">{inr(data.revenue.by_source[src.key])}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-secondary-200 bg-white p-4">
              <h2 className="font-semibold text-secondary-900">Subscriptions today</h2>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><p className="text-secondary-500">Paying clinics</p><p className="text-lg font-semibold">{s.paying}</p></div>
                <div><p className="text-secondary-500">On free trial</p><p className="text-lg font-semibold">{s.on_trial}</p></div>
                <div><p className="text-secondary-500">Monthly recurring</p><p className="text-lg font-semibold">{inr(s.monthly_recurring)}</p></div>
                <div><p className="text-secondary-500">Renewal due soon</p><p className="text-lg font-semibold text-amber-700">{s.expiring_soon}</p></div>
                <div><p className="text-secondary-500">Trials ending in 7 days</p><p className="text-lg font-semibold text-amber-700">{s.trials_ending_7_days}</p></div>
                <div><p className="text-secondary-500">Locked</p><p className="text-lg font-semibold text-red-600">{s.locked}</p></div>
              </div>
              <p className="text-xs text-secondary-500 mt-3">
                Monthly recurring is each paying clinic's plan price (with AI add-on) spread to a 30-day month, GST included.
                {s.no_subscription > 0 && ` ${s.no_subscription} organisation(s) have no subscription.`}{" "}
                <Link to="/admin/organizations" className="text-primary-700 hover:underline">Organisations</Link>
              </p>
            </div>
          </section>

          <section className="rounded-xl border border-secondary-200 bg-white p-4">
            <h2 className="font-semibold text-secondary-900">Last 6 months</h2>
            <div className="h-64 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#4f46e5" />
                  <Bar dataKey="ai_cost" name="AI cost" fill="#f59e0b" />
                  <Bar dataKey="profit" name="Profit" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="rounded-xl border border-secondary-200 bg-white p-4">
            <h2 className="font-semibold text-secondary-900">By clinic</h2>
            {data.by_org.length === 0 ? (
              <p className="text-sm text-secondary-500 mt-2">No clinic paid anything or used AI in this period.</p>
            ) : (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-secondary-500">
                      <th className="py-2 pr-4 font-medium">Clinic</th>
                      <th className="py-2 pr-4 font-medium text-right">Revenue</th>
                      <th className="py-2 pr-4 font-medium text-right">AI cost</th>
                      <th className="py-2 font-medium text-right">Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100">
                    {data.by_org.map((o) => (
                      <tr key={o.org_id}>
                        <td className="py-2 pr-4 text-secondary-900">{o.org_name}</td>
                        <td className="py-2 pr-4 text-right">{inr(o.revenue)}</td>
                        <td className="py-2 pr-4 text-right">{inr(o.ai_cost)}</td>
                        <td className={`py-2 text-right font-medium ${o.profit < 0 ? "text-red-600" : "text-emerald-700"}`}>{inr(o.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-secondary-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-semibold text-secondary-900">Patient payments held for clinics</h2>
              <Link to="/admin/payouts" className="text-sm text-primary-700 hover:underline">Org Payouts</Link>
            </div>
            <p className="text-xs text-secondary-500 mt-1">This is the clinics' money, not NL Labs' income.</p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-secondary-500">Collected online</p><p className="text-lg font-semibold">{inr(pm.collected)}</p><p className="text-xs text-secondary-500">{pm.payments} payments, {inr(pm.gateway_fees)} gateway fees</p></div>
              <div><p className="text-secondary-500">Paid out</p><p className="text-lg font-semibold">{inr(pm.paid_out)}</p><p className="text-xs text-secondary-500">Completed in this period</p></div>
              <div><p className="text-secondary-500">In payout batches</p><p className="text-lg font-semibold">{inr(pm.in_payout_batches)}</p><p className="text-xs text-secondary-500">Started, not completed</p></div>
              <div><p className="text-secondary-500">Not yet in a payout</p><p className="text-lg font-semibold text-amber-700">{inr(pm.awaiting_payout)}</p><p className="text-xs text-secondary-500">All time, after gateway fees</p></div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
